import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { PostTradingEventInterface } from '@modules/options-module/post-trading/interfaces/post-trading-event.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiIcon } from '@taiga-ui/core'
import { Subject, takeUntil } from 'rxjs'
import { TuiSkeleton } from '@taiga-ui/kit'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { PostTradingEventTypeEnum } from '@modules/options-module/post-trading/enums/post-trading-event-type.enum'
import { HttpErrorResponse } from '@angular/common/http'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-post-trading-event-info',
	templateUrl: './post-trading-event-info.component.html',
	styleUrl: './post-trading-event-info.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		TuiIcon,
		TuiSkeleton,
		DatePipe,
		HighlightDirective,
		UpperCasePipe,
		CompanyLogoComponent,
		PricePipe,
		QuantityPipe,
	],
})
export class PostTradingEventInfoComponent implements OnChanges, OnDestroy {
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly postTradingEventTypeEnum: typeof PostTradingEventTypeEnum = PostTradingEventTypeEnum

	@Input() postTradingEvent: PostTradingEventInterface | null = null
	@Output() closeDropdown: EventEmitter<void> = new EventEmitter()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal(null)
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal(false)
	private _getAssetsInfo(): void {
		const symbol: string | undefined = this.postTradingEvent?.ticker
		if (!symbol) return

		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(symbol)
			.pipe(takeUntil(this._destroy$))
			.subscribe({
				next: (assets: AssetExanteInterface): void => {
					this.assetsInfo.set(assets)
					this.isLoadingAssetInfo.set(false)
				},
				error: (error: HttpErrorResponse): void => {
					this.isLoadingAssetInfo.set(false)
					this._alertService.error(
						error.error.error.message ??
							this._translateService.instant('postTrading.info.assetsInfoFailed'),
					)
				},
			})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['postTradingEvent']) {
			this._getAssetsInfo()
		}
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
